import React, { useState, useEffect, useRef } from "react";
import { Row, Col, AutoComplete } from "antd";
import moment from "moment";
import {
  parseFormControlValue,
  isAllowedValue,
  formatNumber,
  removeThousandSeparator,
  getWindowWidth,
  isMobileScreen
} from "../../../services/utils";
import RangeInput from "./RangeInput";
import styles from "./rangeControl.scss";
import { PageRoute } from "../../../models";

const thousands = formatNumber(1000).slice(1, 2);

const fromField = "fromField";
const toField = "toField";

const RangeControl = props => {
  const {
    title,
    suggestedValues,
    numberFormatted,
    hideSuggestions,
    showPlaceholder,
    forbidden,
    showAllControls,
    placeholderFrom,
    placeholderTo,
    showTitle
  } = props;

  const parseValue = props => {
    const { value, fromField, toField, isDateValue } = props;
    const parsedValue = [null, null];

    if (value && isAllowedValue(value[fromField])) {
      parsedValue[0] = isDateValue
        ? new Date(value[fromField]).getUTCFullYear()
        : value[fromField];
    }

    if (value && isAllowedValue(value[toField])) {
      parsedValue[1] = isDateValue
        ? new Date(value[toField]).getUTCFullYear()
        : value[toField];
    }

    return parsedValue;
  };

  const [state, setState] = useState({
    suggestions: [],
    searhPage: false
  });

  const val = useRef(parseValue(props));

  const getSuggestedValue = value => {
    const { numberFormatted } = props;

    return {
      value,
      text: numberFormatted ? formatNumber(value) : value.toString()
    };
  };

  useEffect(() => {
    setState(prev => ({
      ...prev,
      searhPage: window.location.pathname.includes(PageRoute.search)
        ? true
        : false
    }));

    if (hideSuggestions) return;

    if (suggestedValues) {
      setState(prev => ({
        ...prev,
        suggestions: suggestedValues.map(s =>
          numberFormatted ? formatNumber(s) : s.toString()
        )
      }));
    } else {
      const suggestions = buildSuggestions();
      setState(prev => ({ ...prev, suggestions }));
    }
  }, []);

  const buildSuggestions = () => {
    const { min, max, step, isDateValue } = props;
    const maxSuggestion = isDateValue && !max ? new Date().getFullYear() : max;
    const suggestions = [];
    let current = min;

    if (isDateValue) {
      current = maxSuggestion;

      while (current > min) {
        suggestions.push(getSuggestedValue(current));
        current -= step;
      }
      suggestions.push(getSuggestedValue(min));
    } else {
      while (current < maxSuggestion) {
        suggestions.push(getSuggestedValue(current));
        current += step;
      }
      suggestions.push(getSuggestedValue(maxSuggestion));
    }

    return suggestions;
  };

  useEffect(() => {
    if (props.value !== val.current) {
      const value = parseValue(props);
      val.current = value;
    }
  }, [props.value]);

  const onAfterChange = () => {
    const { fromField, toField, onChange, isDateValue } = props;
    const values = [...val.current];

    const parsedValue = parseFormControlValue(
      [fromField, toField],
      !isDateValue
        ? values
        : values.map((value, index) =>
            value
              ? index == 1
                ? moment
                    .utc({ year: value })
                    .endOf("year")
                    .toISOString()
                : moment.utc({ year: value }).toISOString()
              : undefined
          )
    );

    const result = {
      [fromField]: undefined,
      [toField]: undefined,
      ...parsedValue
    };

    onChange(removeFormatNumber(result));
  };

  const removeFormatNumber = value => {
    const { numberFormatted } = props;
    const result = { ...value };

    if (numberFormatted) {
      const propNames = Object.keys(result);
      propNames.forEach(propName => {
        if (result[propName]) {
          result[propName] = removeThousandSeparator(
            result[propName],
            thousands
          );
        }
      });
    }

    return result;
  };

  const onFromChange = value => {
    val.current = [value, val.current[1]];
    onAfterChange();
  };

  const onToChange = value => {
    val.current = [val.current[0], value];
    onAfterChange();
  };

  const { suggestions, searhPage } = state;
  const isMobile = isMobileScreen(getWindowWidth());

  return (
    <React.Fragment>
      {showPlaceholder
        ? showTitle
          ? isMobile && title && <span className="control-title">{title}</span>
          : null
        : title && <span className="control-title">{title}</span>}
      {/* 
        // @ts-ignore */}
      <Row
        gutter={{ xs: 14, sm: 14 }}
        className={showPlaceholder ? styles.inputs : styles.inputsWithTitle}
      >
        {showAllControls ? (
          <Col span={12}>
            <AutoComplete
              onSearch={val => onFromChange(val)}
              value={val.current[0] && val.current[0].toString()}
              onSelect={val => onFromChange(val)}
              dataSource={suggestions}
            >
              <RangeInput
                placeholder={
                  showTitle && isMobile
                    ? "od"
                    : showPlaceholder
                    ? placeholderFrom
                      ? placeholderFrom
                      : `${title} ${"od"}`
                    : "od"
                }
                numberFormatted={numberFormatted}
              />
            </AutoComplete>
          </Col>
        ) : forbidden == fromField && !searhPage ? null : (
          <Col span={forbidden == toField && !searhPage ? 24 : 12}>
            <AutoComplete
              onSearch={val => onFromChange(val)}
              value={val.current[0] && val.current[0].toString()}
              onSelect={val => onFromChange(val)}
              dataSource={suggestions}
            >
              <RangeInput
                placeholder={
                  showTitle && isMobile
                    ? "od"
                    : showPlaceholder
                    ? placeholderFrom
                      ? placeholderFrom
                      : `${title} ${"od"}`
                    : "od"
                }
                numberFormatted={numberFormatted}
              />
            </AutoComplete>
          </Col>
        )}

        {showAllControls ? (
          <Col span={12}>
            <AutoComplete
              dataSource={suggestions}
              onSearch={val => onToChange(val)}
              value={val.current[1] && val.current[1].toString()}
              onSelect={val => onToChange(val)}
            >
              <RangeInput
                placeholder={
                  showTitle && isMobile
                    ? "do"
                    : showPlaceholder
                    ? placeholderTo
                      ? placeholderTo
                      : `${title} ${"do"}`
                    : "do"
                }
                numberFormatted={numberFormatted}
              />
            </AutoComplete>
          </Col>
        ) : forbidden == toField && !searhPage ? null : (
          <Col span={forbidden == fromField && !searhPage ? 24 : 12}>
            <AutoComplete
              dataSource={suggestions}
              onSearch={val => onToChange(val)}
              value={val.current[1] && val.current[1].toString()}
              onSelect={val => onToChange(val)}
            >
              <RangeInput
                placeholder={
                  showTitle && isMobile
                    ? "do"
                    : showPlaceholder
                    ? placeholderTo
                      ? placeholderTo
                      : `${title} ${"do"}`
                    : "do"
                }
                numberFormatted={numberFormatted}
              />
            </AutoComplete>
          </Col>
        )}
      </Row>
    </React.Fragment>
  );
};

RangeControl.defaultProps = {
  isDateValue: false,
  title: "",
  onChange: () => null,
  suggestedValues: null,
  numberFormatted: true,
  hideSuggestions: false,
  showPlaceholder: false
};

export default RangeControl;



// WEBPACK FOOTER //
// ./src/components/FormControls/RangeControl/RangeControl.tsx